import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';
import { H3 } from 'components/core/Typography';
import { Button } from 'components/core/Button';
import {
	Modal,
	ModalBody,
	ModalTitle,
	ModalFooter,
} from 'components/core/Modal';
import { FiltersList } from 'components/search/SearchFilters';
import resetSearchParams from 'utils/resetSearchParams';
import defaultUnitFilters from 'config/filters';
import { useLocation } from 'react-router-dom';
import { TrackingClient } from '@sparefoot/tracking-client';

import './SearchFiltersModal.scss';

export function SearchFiltersModal({
	showing,
	onClose,
	title,
	onFilter,
	searchParams,
	searchType,
	storageTypeUrls,
	facetCounts,
	useNewSearch,
	unitFilters,
}) {
	const [storageType, setStorageType] = useState(searchType);
	const [firstRender, setFirstRender] = useState(true);
	const location = useLocation();

	// when we change the storage type, update the search type in search params
	// so when the user actually applies the filters, it does the correct search type.

	React.useEffect(() => {
		if (firstRender) {
			// so that it does not run when the component initializes
			setFirstRender(false);
		} else if (storageType === 'storage') {
			const cleanVehicleParams = resetSearchParams(
				defaultUnitFilters,
				'vehicle',
			);
			onFilter({ type: storageType, ...cleanVehicleParams }, true);
		} else if (storageType === 'vehicle') {
			const cleanStorageParams = resetSearchParams(
				defaultUnitFilters,
				'storage',
			);
			onFilter({ type: storageType, ...cleanStorageParams }, true);
		}
	}, [storageType]); // eslint-disable-line react-hooks/exhaustive-deps

	// store these for consistency w/ the tabs/button
	// and so we can use state to pick the correct one for the button
	const storageTypeNames = {
		storage: 'Self-Storage',
		vehicle: 'Vehicle Storage',
	};

	const trackSearchFilters = () => {
		const queryParams = new URLSearchParams(location.search);
		const queryParamsObject = {};
		queryParams.forEach((value, key) => {
			if (key !== 'page' && key !== 'listingsPerPage') {
				if (value) {
					queryParamsObject[key] = value;
				}
			}
		});
		TrackingClient.trackSearchFiltered({
			...queryParamsObject,
			search_term: queryParamsObject.location,
		});
	};

	const handleApplyButtonClick = () => {
		// Check if any non-location query params are applied.
		// Keep internal/pagination keys out of this check, and never let `filtered`
		// self-trigger `hasFilters`.
		const hasFilters = Object.keys(searchParams).some((key) => {
			const value = searchParams[key];

			// Skip if value is empty
			if (!value || (Array.isArray(value) && value.length === 0)) {
				return false;
			}

			// Exclude non-filter/internal query params.
			const excludedParams = [
				'location',
				'page',
				'listingsPerPage',
				'filtered',
			];
			if (excludedParams.includes(key)) return false;

			// Any other param with a value indicates a filter is applied
			return true;
		});

		// Add filtered=true if filters are applied, otherwise clear it.
		if (hasFilters) {
			onFilter({ filtered: 'true' });
		} else {
			onFilter({ filtered: null });
		}

		trackSearchFilters();
		scrollToElement('.search-page');
		onClose();
	};

	const handleClose = () => {
		const cleanParams = resetSearchParams(defaultUnitFilters, storageType);
		onFilter({ type: storageType, ...cleanParams }, true);
		onClose();
	};

	return (
		<Modal
			className="search-filters-modal"
			showing={showing}
			onClose={handleClose}
			hasDarkTitleBackground
		>
			<ModalTitle bgColor={useNewSearch ? 'navyBlue' : 'secondaryBlue'}>
				<H3
					last
					size="medium"
					color="white"
				>
					{title}
				</H3>
			</ModalTitle>
			<ModalBody noPadding>
				<div className="filter-tabs">
					<Button
						type="button"
						className={classnames('filter-tab', {
							selected: storageType === 'storage',
						})}
						onClick={() => setStorageType('storage')}
					>
						{`${storageTypeNames.storage} Filters`}
					</Button>
					<Button
						type="button"
						className={classnames('filter-tab', {
							selected: storageType === 'vehicle',
						})}
						onClick={() => setStorageType('vehicle')}
					>
						{`${storageTypeNames.vehicle} Filters`}
					</Button>
				</div>
				<FiltersList
					listKey="search-modal-filters"
					showing={showing}
					onClose={handleClose}
					onFilter={onFilter}
					delayFiltering
					searchParams={searchParams}
					searchType={storageType}
					storageTypeUrls={storageTypeUrls}
					facetCounts={facetCounts}
					useNewSearch={useNewSearch}
					unitFilters={unitFilters}
				/>
			</ModalBody>
			<ModalFooter bgColor={useNewSearch ? 'navyBlue' : 'secondaryBlue'}>
				<Button
					onClick={handleApplyButtonClick}
					primary={!useNewSearch}
					secondary={useNewSearch}
					fullWidth
				>
					{`Apply ${storageTypeNames[storageType]} Filters`}
				</Button>
			</ModalFooter>
		</Modal>
	);
}

SearchFiltersModal.propTypes = {
	showing: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	title: PropTypes.string,
	onFilter: PropTypes.func.isRequired,
	searchParams: PropTypes.object.isRequired,
	searchType: PropTypes.string,
	storageTypeUrls: PropTypes.object,
	facetCounts: PropTypes.object,
	useNewSearch: PropTypes.bool,
	unitFilters: PropTypes.object,
};

export default SearchFiltersModal;
